export type SseConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting";

export interface SseCoreConfig {
  baseURL: string;
  userId: string;
  aiThreadId: string;
  token: string;
  connectionId?: string;
  withCredentials?: boolean;
}

export interface SseCoreConnection {
  isConnected: boolean;
  error: string | null;
  reconnectAttempts: number;
  connectionId: string | null;
  lastEventId: string | null;
  eventSource: EventSource | null;
  reconnectTimeout: ReturnType<typeof setTimeout> | null;
}

export interface SseCoreHandlers<TMessage extends { type: string }> {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onReconnecting?: (attempt: number) => void;
  onError?: (error: string) => void;
  onMessage?: (message: TMessage) => void;
  onStreamStart?: (data?: any) => void;
  onStreamEnd?: (data?: any) => void;
  onUpdate?: (update: string, data?: any) => void;
  onTicketRefresh?: () => void;
  onHumanReview?: (data: any) => void;
  onHumanReviewRequest?: (data: any) => void;
  onHumanReviewStatus?: (status: string) => void;
  onThreadDeleted?: (aiThreadId: string) => void;
  onThreadName?: (threadName: string) => void;
  onPlanProgress?: (planProgress: any) => void;
}

export interface SseCoreLock {
  claim: () => boolean;
  has: () => boolean;
  release: () => void;
}

export interface SseCoreBehavior {
  createConnectionId: (args: {
    aiThreadId: string;
    reconnectAttempts: number;
    configConnectionId?: string;
  }) => string;
  preserveConfigConnectionIdOnFirstConnect: boolean;
  aiChunkMessageType: string;
  touchHeartbeatOnEvents: boolean;
  requireCurrentEventSource: boolean;
  blockConnectWhileEventSourceConnecting: boolean;
  enforceLock: boolean;
  pingMismatchState: SseConnectionStatus;
  heartbeatFailureState: SseConnectionStatus;
  errorState: SseConnectionStatus;
  callOnDisconnectedOnPingMismatch: boolean;
  callOnDisconnectedOnHeartbeatFailure: boolean;
  callOnDisconnectedOnError: boolean;
  callOnDisconnectedOnMaxReconnect: boolean;
  includeMessageProcessedEvent: boolean;
  includeMessageErrorEvent: boolean;
  humanReviewRequestMode: "message" | "humanReview";
  formatHumanReviewRequest?: (data: any) => any;
  initialLastHeartbeat: number;
}

export class SseManagerCore<TMessage extends { type: string }> {
  private config: SseCoreConfig;
  private handlers: SseCoreHandlers<TMessage>;
  private connection: SseCoreConnection;
  private lock: SseCoreLock | null;
  private behavior: SseCoreBehavior;
  private maxReconnectAttempts = 10;
  private baseReconnectDelay = 2000;
  private maxReconnectDelay = 30000;
  private readonly HEARTBEAT_STALE_MS = 45000;
  private readonly HEARTBEAT_CHECK_MS = 5000;
  private lastHeartbeat: number;
  private heartbeatTimer: number | null = null;
  private connectionState: SseConnectionStatus = "disconnected";
  private messageQueue: Array<{ data: TMessage; timestamp: number }> = [];
  private isDestroyed = false;
  private isConnecting = false;

  constructor(
    config: SseCoreConfig,
    handlers: SseCoreHandlers<TMessage>,
    behavior: SseCoreBehavior,
    lock?: SseCoreLock,
  ) {
    this.config = config;
    this.handlers = handlers;
    this.behavior = behavior;
    this.lock = lock ?? null;
    this.lastHeartbeat = behavior.initialLastHeartbeat;
    this.connection = {
      isConnected: false,
      error: null,
      reconnectAttempts: 0,
      connectionId: config.connectionId || null,
      lastEventId: null,
      eventSource: null,
      reconnectTimeout: null,
    };
  }

  public updateHandlers(handlers: SseCoreHandlers<TMessage>): void {
    this.handlers = handlers;
  }

  public connect(): void {
    if (this.isDestroyed) return;
    if (!this.config.aiThreadId || !this.config.userId) return;
    if (!this.claimLockIfNeeded()) return;

    const actuallyConnected = this.isConnected();
    if (this.connectionState === "connected" && actuallyConnected) return;
    if (
      this.isConnecting ||
      this.connectionState === "connecting" ||
      this.connectionState === "reconnecting"
    ) {
      return;
    }

    if (this.connection.reconnectTimeout) {
      clearTimeout(this.connection.reconnectTimeout);
      this.connection.reconnectTimeout = null;
    }

    if (
      this.behavior.blockConnectWhileEventSourceConnecting &&
      this.connection.eventSource &&
      this.connection.eventSource.readyState === EventSource.CONNECTING
    ) {
      return;
    }

    this.isConnecting = true;
    this.connectionState = "connecting";
    this.establishConnection();
  }

  public disconnect(): void {
    this.isDestroyed = true;
    this.cleanup();
  }

  public getConnectionState(): SseCoreConnection {
    return { ...this.connection };
  }

  public isConnected(): boolean {
    const isEventSourceOpen =
      this.connection.eventSource?.readyState === EventSource.OPEN;
    const timeSinceLastHeartbeat =
      this.lastHeartbeat > 0 ? Date.now() - this.lastHeartbeat : Infinity;
    const hasRecentHeartbeat =
      this.lastHeartbeat > 0 && timeSinceLastHeartbeat < this.HEARTBEAT_STALE_MS;
    return Boolean(isEventSourceOpen && hasRecentHeartbeat);
  }

  public getConnectionStatus(): SseConnectionStatus {
    return this.connectionState;
  }

  private establishConnection(): void {
    if (this.isDestroyed) return;
    if (!this.claimLockIfNeeded()) {
      this.isConnecting = false;
      this.connectionState = "disconnected";
      return;
    }

    const hasRecentHeartbeat =
      this.lastHeartbeat > 0 &&
      Date.now() - this.lastHeartbeat < this.HEARTBEAT_STALE_MS;
    if (
      this.connection.connectionId &&
      this.connection.eventSource?.readyState === EventSource.OPEN &&
      hasRecentHeartbeat
    ) {
      return;
    }

    this.closeCurrentEventSource();

    try {
      const aiThreadId = String(this.config.aiThreadId);
      if (!this.connection.connectionId) {
        if (
          this.behavior.preserveConfigConnectionIdOnFirstConnect &&
          this.config.connectionId &&
          this.connection.reconnectAttempts === 0
        ) {
          this.connection.connectionId = this.config.connectionId;
        } else {
          this.connection.connectionId = this.behavior.createConnectionId({
            aiThreadId,
            reconnectAttempts: this.connection.reconnectAttempts,
            configConnectionId: this.config.connectionId,
          });
        }
      }

      const params = new URLSearchParams({
        user_id: this.config.userId,
        token: this.config.token,
        connection_id: this.connection.connectionId,
      });
      if (this.connection.lastEventId) {
        params.set("last_event_id", this.connection.lastEventId);
        params.set("skip_replay", "false");
      } else {
        params.set("skip_replay", "true");
      }

      const sseUrl = `${this.config.baseURL}/sse/chat/${aiThreadId}?${params.toString()}`;

      const eventSource = new EventSource(sseUrl, {
        withCredentials: this.config.withCredentials ?? true,
      });

      this.connection.eventSource = eventSource;
      this.setupEventHandlers(eventSource);
    } catch (error) {
      this.isConnecting = false;
      this.connection.error = "Failed to connect";
      this.connectionState = "disconnected";
      this.handlers.onError?.("Failed to connect");
      this.handleReconnection();
    }
  }

  private setupEventHandlers(eventSource: EventSource): void {
    const ensureCurrentEventSource = () => {
      if (!this.behavior.requireCurrentEventSource) return true;
      if (this.connection.eventSource === eventSource) return true;
      try {
        eventSource.close();
      } catch {}
      return false;
    };

    const touchHeartbeat = () => {
      this.lastHeartbeat = Date.now();
    };

    const updateReplayCursor = (parsedData: any) => {
      const explicitEntryId =
        parsedData && typeof parsedData === "object"
          ? parsedData.entry_id
          : undefined;
      if (typeof explicitEntryId === "string" && explicitEntryId.trim()) {
        this.connection.lastEventId = explicitEntryId.trim();
      }
    };

    const beforeEvent = (): boolean => {
      if (!ensureCurrentEventSource()) return false;
      if (this.behavior.touchHeartbeatOnEvents) touchHeartbeat();
      return true;
    };

    eventSource.onopen = () => {
      if (this.behavior.enforceLock && !this.hasLockIfNeeded()) {
        try {
          eventSource.close();
        } catch {}
        return;
      }
      if (!ensureCurrentEventSource()) return;
      if (eventSource.readyState !== EventSource.OPEN) return;

      this.isConnecting = false;
      this.connection.isConnected = true;
      this.connection.error = null;
      this.connection.reconnectAttempts = 0;
      this.connectionState = "connected";
      touchHeartbeat();
      this.handlers.onConnected?.();
      this.processMessageQueue();

      if (this.heartbeatTimer) {
        window.clearInterval(this.heartbeatTimer);
      }

      this.heartbeatTimer = window.setInterval(() => {
        if (this.isDestroyed) return;
        if (this.behavior.enforceLock && !this.hasLockIfNeeded()) {
          if (this.heartbeatTimer) {
            window.clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
          }
          this.closeCurrentEventSource();
          this.isConnecting = false;
          this.connection.isConnected = false;
          this.connectionState = "disconnected";
          return;
        }

        const isOpen = this.connection.eventSource?.readyState === EventSource.OPEN;
        const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeat;
        if (!isOpen || timeSinceLastHeartbeat > this.HEARTBEAT_STALE_MS) {
          if (this.heartbeatTimer) {
            window.clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
          }
          this.connection.isConnected = false;
          this.connectionState = this.behavior.heartbeatFailureState;
          this.lastHeartbeat = 0;

          this.closeCurrentEventSource();
          this.connection.connectionId = null;
          if (this.behavior.callOnDisconnectedOnHeartbeatFailure) {
            this.handlers.onDisconnected?.();
          }
          this.handleReconnection();
        }
      }, this.HEARTBEAT_CHECK_MS);
    };

    eventSource.onmessage = null as any;

    eventSource.addEventListener("reconnect", () => {
      if (!ensureCurrentEventSource() || this.isDestroyed) return;
      this.isConnecting = false;
      this.connection.isConnected = false;
      this.lastHeartbeat = 0;
      if (this.heartbeatTimer) {
        window.clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }
      this.closeCurrentEventSource(eventSource);
      this.connection.connectionId = null;
      this.handleReconnection();
    });

    eventSource.addEventListener("ping", (e: MessageEvent) => {
      if (!ensureCurrentEventSource()) return;
      if (eventSource.readyState !== EventSource.OPEN) return;

      try {
        const data = JSON.parse(e.data || "{}");
        const pingConnectionId = data?.connection_id;
        const ourConnectionId = this.connection.connectionId;
        if (
          pingConnectionId &&
          ourConnectionId &&
          pingConnectionId !== ourConnectionId
        ) {
          this.connection.isConnected = false;
          this.connectionState = this.behavior.pingMismatchState;
          this.lastHeartbeat = 0;
          this.closeCurrentEventSource(eventSource);
          if (this.behavior.callOnDisconnectedOnPingMismatch) {
            this.handlers.onDisconnected?.();
          }
          this.handleReconnection();
          return;
        }
      } catch {}

      touchHeartbeat();
    });

    eventSource.addEventListener("user_message", (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data);
        if (!beforeEvent()) return;
        updateReplayCursor(parsed);
        this.handlers.onMessage?.({
          ...parsed,
          type: "user_message",
        } as TMessage);
      } catch {}
    });

    eventSource.addEventListener("user_message_pending", (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data);
        if (!beforeEvent()) return;
        updateReplayCursor(parsed);
        this.handlers.onMessage?.({
          ...parsed,
          type: "user_message_pending",
        } as TMessage);
      } catch {}
    });

    eventSource.addEventListener("message_id_assigned", (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data);
        if (!beforeEvent()) return;
        updateReplayCursor(parsed);
        this.handlers.onMessage?.({
          ...parsed,
          type: "message_id_assigned",
        } as TMessage);
      } catch {}
    });

    eventSource.addEventListener("ai_chunk", (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data);
        if (!beforeEvent()) return;
        updateReplayCursor(parsed);
        this.handlers.onMessage?.({
          ...parsed,
          type: this.behavior.aiChunkMessageType,
          isStreaming: true,
        } as TMessage);
      } catch {}
    });

    eventSource.addEventListener("ai_complete", (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data);
        if (!beforeEvent()) return;
        updateReplayCursor(parsed);
        this.handlers.onMessage?.({
          ...parsed,
          type: "ai_complete",
          isStreaming: false,
        } as TMessage);
      } catch {}
    });

    eventSource.addEventListener("update", (e: MessageEvent) => {
      try {
        if (!beforeEvent()) return;
        const d = JSON.parse(e.data);
        updateReplayCursor(d);
        const t = d.update ?? d.content ?? "";
        if (t) {
          if (t === "refresh") this.handlers.onTicketRefresh?.();
          else this.handlers.onUpdate?.(t, d);
        }
      } catch {}
    });

    eventSource.addEventListener("stream_start", (e: MessageEvent) => {
      let parsed: any = {};
      try {
        parsed = JSON.parse(e.data || "{}");
      } catch {}
      if (!beforeEvent()) return;
      updateReplayCursor(parsed);
      this.handlers.onStreamStart?.(parsed);
    });

    eventSource.addEventListener("stream_end", (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data || "{}");
        if (!beforeEvent()) return;
        updateReplayCursor(parsed);
        if (parsed?.terminal === true) this.handlers.onStreamEnd?.(parsed);
      } catch {}
    });

    eventSource.addEventListener("thread_automation", (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data || "{}");
        if (!beforeEvent()) return;
        updateReplayCursor(parsed);
        this.handlers.onMessage?.({ ...parsed, type: "thread_automation" });
      } catch {}
    });

    eventSource.addEventListener("ssh_event", (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data);
        if (!beforeEvent()) return;
        updateReplayCursor(parsed);
        this.handlers.onMessage?.({
          ...parsed,
          type: "ssh_event",
        } as TMessage);
      } catch {}
    });

    eventSource.addEventListener("computer_use_event", (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data);
        if (!beforeEvent()) return;
        updateReplayCursor(parsed);
        this.handlers.onMessage?.({
          ...parsed,
          type: "computer_use_event",
        } as TMessage);
      } catch {}
    });

    eventSource.addEventListener("human_review_request", (e: MessageEvent) => {
      try {
        if (!beforeEvent()) return;
        const parsed = JSON.parse(e.data);
        updateReplayCursor(parsed);
        if (this.behavior.humanReviewRequestMode === "humanReview") {
          const payload = this.behavior.formatHumanReviewRequest
            ? this.behavior.formatHumanReviewRequest(parsed)
            : parsed;
          this.handlers.onHumanReview?.(payload);
        } else {
          this.handlers.onMessage?.({
            ...parsed,
            type: "human_review_request",
            isStreaming: false,
          } as TMessage);
        }
      } catch {}
    });

    eventSource.addEventListener("thread_deleted", (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data);
        if (!beforeEvent()) return;
        updateReplayCursor(parsed);
        this.handlers.onMessage?.({
          ...parsed,
          type: "thread_deleted",
        } as TMessage);
      } catch {}
    });

    eventSource.addEventListener("human_review_status", (e: MessageEvent) => {
      try {
        if (!beforeEvent()) return;
        const d = JSON.parse(e.data);
        updateReplayCursor(d);
        this.handlers.onHumanReview?.(d);
      } catch {}
    });

    if (this.behavior.includeMessageProcessedEvent) {
      eventSource.addEventListener("message_processed", (e: MessageEvent) => {
        try {
          const parsed = JSON.parse(e.data);
          if (!beforeEvent()) return;
          updateReplayCursor(parsed);
          this.handlers.onMessage?.({
            ...parsed,
            type: "message_processed",
          } as TMessage);
        } catch {}
      });
    }

    if (this.behavior.includeMessageErrorEvent) {
      eventSource.addEventListener("message_error", (e: MessageEvent) => {
        try {
          const parsed = JSON.parse(e.data);
          if (!beforeEvent()) return;
          updateReplayCursor(parsed);
          this.handlers.onMessage?.({
            ...parsed,
            type: "message_error",
          } as TMessage);
        } catch {}
      });
    }

    eventSource.addEventListener("plan_progress", (e: MessageEvent) => {
      try {
        if (!beforeEvent()) return;
        const d = JSON.parse(e.data);
        updateReplayCursor(d);
        this.handlers.onPlanProgress?.(d.plan_progress || d);
      } catch {}
    });

    eventSource.addEventListener("thread_name_update", (e: MessageEvent) => {
      try {
        if (!beforeEvent()) return;
        const d = JSON.parse(e.data || "{}");
        updateReplayCursor(d);
        const threadName =
          typeof d?.thread_name === "string"
            ? d.thread_name.trim()
            : typeof d?.content === "string"
              ? d.content.trim()
              : "";
        if (threadName) {
          this.handlers.onThreadName?.(threadName);
        }
      } catch {}
    });

    eventSource.onerror = () => {
      if (this.behavior.enforceLock && !this.hasLockIfNeeded()) {
        try {
          eventSource.close();
        } catch {}
        return;
      }
      if (!ensureCurrentEventSource()) return;
      if (this.isDestroyed) return;

      this.isConnecting = false;
      this.connection.isConnected = false;
      this.connectionState = this.behavior.errorState;
      this.lastHeartbeat = 0;

      if (this.behavior.callOnDisconnectedOnError) {
        this.handlers.onDisconnected?.();
      }

      if (this.heartbeatTimer) {
        window.clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }

      this.closeCurrentEventSource(eventSource);
      this.connection.connectionId = null;

      let errorMessage = "Connection error";
      if (eventSource.readyState === EventSource.CLOSED) {
        errorMessage = "Server connection closed";
      } else if (eventSource.readyState === EventSource.CONNECTING) {
        errorMessage = "Server connection failed";
      }

      this.connection.error = errorMessage;
      this.handlers.onError?.(errorMessage);

      if (!this.isDestroyed) this.handleReconnection();
    };
  }

  private handleReconnection(): void {
    if (this.isDestroyed) return;
    if (!this.claimLockIfNeeded()) {
      this.isConnecting = false;
      this.connectionState = "disconnected";
      return;
    }

    if (this.connection.reconnectAttempts >= this.maxReconnectAttempts) {
      this.connection.error = "Connection failed after multiple attempts";
      this.connectionState = "disconnected";
      if (this.behavior.callOnDisconnectedOnMaxReconnect) {
        this.handlers.onDisconnected?.();
      }
      this.handlers.onError?.("Connection failed after multiple attempts");
      return;
    }

    if (this.connection.reconnectTimeout) {
      clearTimeout(this.connection.reconnectTimeout);
    }

    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.connection.reconnectAttempts),
      this.maxReconnectDelay,
    );

    this.connectionState = "reconnecting";
    this.handlers.onReconnecting?.(this.connection.reconnectAttempts + 1);

    this.connection.reconnectTimeout = setTimeout(() => {
      if (!this.isDestroyed) {
        this.connection.reconnectAttempts++;
        this.connectionState = "connecting";
        this.establishConnection();
      }
    }, delay);
  }

  private processMessageQueue(): void {
    if (!this.messageQueue.length) return;
    const messages = [...this.messageQueue];
    this.messageQueue = [];
    messages.forEach(({ data, timestamp }) => {
      if (Date.now() - timestamp < 30000) {
        this.handlers.onMessage?.(data);
      }
    });
  }

  private cleanup(): void {
    if (this.connection.reconnectTimeout) {
      clearTimeout(this.connection.reconnectTimeout);
      this.connection.reconnectTimeout = null;
    }

    this.closeCurrentEventSource();

    if (this.heartbeatTimer) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }

    this.connection.isConnected = false;
    this.connection.error = null;
    this.connection.reconnectAttempts = 0;
    this.connection.connectionId = null;
    this.connection.lastEventId = null;
    this.connectionState = "disconnected";
    this.messageQueue = [];
    this.isConnecting = false;
    this.releaseLockIfNeeded();
    this.handlers.onDisconnected?.();
  }

  private closeCurrentEventSource(target?: EventSource): void {
    const source = target ?? this.connection.eventSource;
    if (!source) return;
    if (!target || this.connection.eventSource === source) {
      this.connection.eventSource = null;
    }
    try {
      source.close();
    } catch {}
  }

  private claimLockIfNeeded(): boolean {
    if (!this.behavior.enforceLock) return true;
    return this.lock?.claim() ?? true;
  }

  private hasLockIfNeeded(): boolean {
    if (!this.behavior.enforceLock) return true;
    return this.lock?.has() ?? true;
  }

  private releaseLockIfNeeded(): void {
    if (!this.behavior.enforceLock) return;
    this.lock?.release();
  }
}
